/**
 * SlackChannelPicker / TeamsChannelPicker — reusable channel selectors that
 * replace pasted webhook URLs everywhere INZARA delivers notifications
 * (Executive Summaries, Morning Brief, Alerts, …).
 *
 * Slack channels come from the tenant's connected Slack app (Bot Token →
 * conversations.list). Teams channels are the channels the tenant's Teams bot
 * can post to. Both degrade gracefully with a "connect first" hint when the
 * integration isn't set up yet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hash, Lock, Users, Loader2, RefreshCw, Send, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

function Picker({ kind, Icon, fetchChannels, testChannel, value, onChange, multiple = true, hint }) {
  const [channels, setChannels] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState('');
  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetchChannels();
      setChannels(res.channels || []);
    } catch (e) {
      setError(e.message || `${kind} is not connected yet.`);
      setChannels([]);
    } finally { setLoading(false); }
  }, [fetchChannels, kind]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    if (multiple) {
      onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    } else {
      onChange(selected.includes(id) ? '' : id);
    }
  };

  const sendTest = async (id) => {
    setTesting(id);
    try {
      await testChannel(id);
      toast.success(`Test ${kind} message sent`);
    } catch (e) {
      toast.error(e.message || `${kind} test failed`);
    } finally { setTesting(''); }
  };

  const testid = kind.toLowerCase();

  if (loading) {
    return (
      <div className="rounded-lg bg-muted/30 border border-border/50 p-3 flex items-center gap-2 text-xs text-muted-foreground" data-testid={`${testid}-picker-loading`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {kind} channels…
      </div>
    );
  }

  if (error || !channels || channels.length === 0) {
    return (
      <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-3 space-y-1.5" data-testid={`${testid}-picker-empty`}>
        <div className="flex items-center gap-1.5 text-xs text-amber-300">
          <AlertCircle className="w-3.5 h-3.5" /> {kind} not connected
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">{error || hint}</p>
        <div className="flex items-center gap-2 pt-0.5">
          <Link to="/settings" className="text-[11px] text-primary hover:underline" data-testid={`${testid}-connect-link`}>Open Settings → Integrations</Link>
          <button onClick={load} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid={`${testid}-picker-retry`}>
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-muted/30 border border-border/50 p-1.5 max-h-44 overflow-y-auto space-y-0.5" data-testid={`${testid}-channel-picker`}>
      {channels.map((c) => {
        const on = selected.includes(c.id);
        return (
          <div key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors ${on ? 'bg-primary/15' : 'hover:bg-muted/40'}`}>
            <button
              type="button"
              onClick={() => toggle(c.id)}
              className="flex items-center gap-2 flex-1 min-w-0 text-left"
              data-testid={`${testid}-channel-${c.id}`}
            >
              <span className={`w-4 h-4 rounded-[5px] border flex items-center justify-center shrink-0 ${on ? 'bg-primary border-primary text-primary-foreground' : 'border-border/60'}`}>
                {on && <Check className="w-3 h-3" />}
              </span>
              {c.is_private ? <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
              <span className="text-xs text-foreground truncate">{c.name}</span>
            </button>
            <button
              type="button"
              onClick={() => sendTest(c.id)}
              disabled={!!testing}
              title="Send a test message here"
              className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border/50 text-muted-foreground hover:text-foreground hover:border-primary/40 disabled:opacity-50 shrink-0"
              data-testid={`${testid}-test-${c.id}`}
            >
              {testing === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Test
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function SlackChannelPicker(props) {
  return (
    <Picker
      kind="Slack"
      Icon={Hash}
      fetchChannels={api.listSlackChannels}
      testChannel={api.testSlackChannel}
      hint="Connect a Slack app with a Bot User OAuth Token (channels:read, groups:read, chat:write) in Settings → Integrations, then invite the bot to the channels you want."
      {...props}
    />
  );
}

export function TeamsChannelPicker(props) {
  return (
    <Picker
      kind="Teams"
      Icon={Users}
      fetchChannels={api.listTeamsChannels}
      testChannel={api.testTeamsChannel}
      hint="Install the INZARA Teams bot to your team in Settings → Integrations, then @mention it once in each channel you want to post to."
      {...props}
    />
  );
}
